import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X,
  EnvelopeSimple,
  ShieldCheck,
  UserCircle,
  Buildings,
  MapPin,
  Receipt,
  Info,
} from "@phosphor-icons/react";
import { Input, Label } from "../ui/Input";
import { Button } from "../ui/Button";
import { api, extractError } from "../../lib/api";

/**
 * Iter 19.63.15 — Erweitertes Admin-Benutzer-Modal:
 * - „create" Modus: anlegen + Setup-Mail (unverändert seit 19.25).
 * - „edit" Modus: ALLE Stammdaten editierbar (Name, Firma, Adresse,
 *   Rechnungs-Mail, Rolle, Status).
 *
 * Schutzregel (Backend setzt das durch, FE spiegelt es):
 * - Admins (non-superadmin) dürfen NUR `user`-Konten bearbeiten.
 *   Andere Admin/Superadmin-Konten lösen 403 aus → Backend blockiert.
 *   Im FE ist Edit-Button für solche Zeilen disabled (siehe AdminUsersPage).
 *
 * Iter 19.63.16 — Tarif-Labels: kanonische MINI/MIDI/MAXI-Bezeichner
 * (Storage-Werte bleiben `starter/growth/enterprise` für DB-Kompatibilität,
 * Anzeige folgt dem auf RegisterPage etablierten Schema).
 */
const COMPANY_SIZES = [
  { value: "", label: "— nicht gesetzt —" },
  { value: "starter", label: "MINI (bis 50 MA / Kanzlei bis 10 MA)" },
  { value: "growth", label: "MIDI (bis 250 MA / Kanzlei bis 50 MA)" },
  { value: "enterprise", label: "MAXI (über 250 MA / Kanzlei über 50 MA)" },
];

const STATUSES = [
  { value: "ausstehend", label: "Ausstehend (E-Mail-Setup offen)" },
  { value: "aktiv", label: "Aktiv" },
  { value: "abgelehnt", label: "Abgelehnt / Deaktiviert" },
];

export default function UserFormModal({ mode, initialUser, callerRole, currentUserId, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const isSuperadmin = callerRole === "superadmin";

  // Core
  const [email, setEmail] = useState(initialUser?.email || "");
  const [name, setName] = useState(initialUser?.name || "");
  const [role, setRole] = useState(initialUser?.role || "user");
  const [status, setStatus] = useState(initialUser?.status || "ausstehend");

  // Profile (only relevant in edit mode)
  const [firstName, setFirstName] = useState(initialUser?.first_name || "");
  const [lastName, setLastName] = useState(initialUser?.last_name || "");
  const [company, setCompany] = useState(initialUser?.company || "");
  const [companySize, setCompanySize] = useState(initialUser?.company_size || "");

  // Address
  const [street, setStreet] = useState(initialUser?.address_street || "");
  const [postalCode, setPostalCode] = useState(initialUser?.address_postal_code || "");
  const [city, setCity] = useState(initialUser?.address_city || "");

  // Billing
  const [billingDifferent, setBillingDifferent] = useState(Boolean(initialUser?.billing_address_different));
  const [billingEmail, setBillingEmail] = useState(initialUser?.billing_email || "");

  // Iter 19.68.17 — Company-Zuordnung (nur Edit-Modus).
  const [companyId, setCompanyId] = useState(initialUser?.company_id || "");
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/companies");
        setCompanies(data?.items || []);
      } catch (_e) {
        // Nicht-fatal: der Superadmin-Endpoint ist für normale Admins 403.
        // In dem Fall wird die Dropdown einfach nicht gezeigt.
      }
    })();
  }, [isEdit]);

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Esc closes
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const isSelf = isEdit && initialUser?.id && currentUserId && initialUser.id === currentUserId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const emailTrim = email.trim().toLowerCase();
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(emailTrim)) {
      setErr("Bitte eine gültige E-Mail-Adresse eingeben.");
      return;
    }
    if (billingEmail && !/^[^@]+@[^@]+\.[^@]+$/.test(billingEmail.trim())) {
      setErr("Bitte eine gültige Rechnungs-E-Mail eingeben (oder das Feld leer lassen).");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit) {
        const payload = {};
        const addField = (key, value, original) => {
          if (value !== original) payload[key] = value;
        };
        if (emailTrim !== initialUser.email) payload.email = emailTrim;
        addField("name", name.trim(), initialUser.name || "");
        addField("first_name", firstName.trim(), initialUser.first_name || "");
        addField("last_name", lastName.trim(), initialUser.last_name || "");
        addField("company", company.trim(), initialUser.company || "");
        addField("company_size", companySize || "", initialUser.company_size || "");
        addField("address_street", street.trim(), initialUser.address_street || "");
        addField("address_postal_code", postalCode.trim(), initialUser.address_postal_code || "");
        addField("address_city", city.trim(), initialUser.address_city || "");
        const bDiff = Boolean(billingDifferent);
        if (bDiff !== Boolean(initialUser.billing_address_different)) {
          payload.billing_address_different = bDiff;
        }
        const bEmailTrim = billingEmail.trim().toLowerCase();
        if (bEmailTrim !== (initialUser.billing_email || "")) {
          // Backend EmailStr requires a valid mail OR null; we send null when empty.
          payload.billing_email = bEmailTrim || null;
        }
        if (role !== initialUser.role) payload.role = role;
        if (status !== initialUser.status) payload.status = status;
        // Iter 19.68.17 — Company-Zuordnung ändern. Sentinel "__none__" =
        // Zuordnung entfernen.
        const newCid = companyId || "";
        const oldCid = initialUser.company_id || "";
        if (newCid !== oldCid) {
          payload.company_id = newCid || "__none__";
        }

        // Strip null/empty-string vs original for company-size which is nullable.
        if (Object.keys(payload).length === 0) {
          toast.info("Keine Änderungen.");
          setSubmitting(false);
          return;
        }
        const { data } = await api.patch(`/admin/users/${initialUser.id}`, payload);
        toast.success(`Benutzer aktualisiert (${data.changes?.length || 0} Felder geändert).`);
        onSaved?.(data.user);
      } else {
        const { data } = await api.post("/admin/users", {
          email: emailTrim,
          name: name.trim(),
          role,
        });
        if (data.mail_sent) {
          toast.success(
            `Benutzer angelegt — Einladungs-Mail an ${emailTrim} versendet (4 Tage gültig).`,
          );
        } else {
          toast.warning(
            `Benutzer angelegt, aber Einladungs-Mail konnte nicht versendet werden: ${
              data.mail_info || "unbekannter Fehler"
            }. Bitte „Setup-Mail erneut senden" verwenden.`,
          );
        }
        onSaved?.(data.user);
      }
      onClose();
    } catch (e2) {
      setErr(extractError(e2, isEdit ? "Aktualisierung fehlgeschlagen." : "Anlegen fehlgeschlagen."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`admin-user-${mode}-modal`}
      className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-ink-900/60 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl my-4 bg-white border border-ink-200 rounded-sm shadow-xl"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-6 py-4 border-b border-ink-200 bg-white rounded-t-sm">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
              {isEdit ? "Benutzer bearbeiten" : "Neuen Benutzer anlegen"}
            </div>
            <h3 className="font-cabinet text-xl text-ink-900 tracking-tight font-medium truncate">
              {isEdit ? initialUser?.email : "Account einrichten"}
            </h3>
            {!isEdit && (
              <p className="text-[12px] text-warm-700 mt-1.5 leading-relaxed">
                Der Benutzer erhält eine E-Mail mit einem Einladungs-Link
                (4 Tage gültig) und vergibt beim ersten Login selbst sein Passwort.
              </p>
            )}
            {isEdit && isSelf && (
              <p className="text-[12px] text-amber-700 mt-1.5 leading-relaxed flex items-start gap-1.5">
                <Info size={12} weight="bold" className="shrink-0 mt-0.5" />
                Sie bearbeiten Ihr eigenes Konto. Sie können sich nicht selbst
                deaktivieren.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Schließen"
            className="text-warm-600 hover:text-ink-900 transition-crisp"
            data-testid="admin-user-form-close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Section: Account-Identifikation */}
          <section>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2 flex items-center gap-1.5">
              <EnvelopeSimple size={11} weight="bold" />
              Account
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label htmlFor="admin-user-email">E-Mail (Login)</Label>
                <Input
                  id="admin-user-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="benutzer@firma.at"
                  required
                  autoFocus={!isEdit}
                  data-testid="admin-user-form-email"
                />
              </div>
              <div>
                <Label htmlFor="admin-user-name">
                  Voller Name <span className="text-warm-500 font-normal normal-case">(optional)</span>
                </Label>
                <Input
                  id="admin-user-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Max Mustermann"
                  data-testid="admin-user-form-name"
                />
              </div>
              <div>
                <Label htmlFor="admin-user-role" className="flex items-center gap-1">
                  <ShieldCheck size={11} weight="bold" />
                  Rolle
                </Label>
                <select
                  id="admin-user-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  data-testid="admin-user-form-role"
                  className="w-full text-sm rounded-sm border border-ink-300 px-3 py-2 bg-white focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                >
                  <option value="user">Benutzer</option>
                  <option value="admin">Administrator</option>
                  {isSuperadmin && <option value="superadmin">Superadmin</option>}
                  {!isSuperadmin && role === "superadmin" && (
                    <option value="superadmin" disabled>
                      Superadmin (nur durch Superadmin änderbar)
                    </option>
                  )}
                </select>
                {role === "superadmin" && (
                  <p className="text-[11px] text-warm-600 mt-1 leading-snug">
                    Superadmins können KV bearbeiten und weitere Superadmins ernennen.
                  </p>
                )}
              </div>
              {isEdit && (
                <div className="sm:col-span-2">
                  <Label htmlFor="admin-user-status">Status</Label>
                  <select
                    id="admin-user-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    data-testid="admin-user-form-status"
                    disabled={isSelf}
                    className="w-full text-sm rounded-sm border border-ink-300 px-3 py-2 bg-white focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 disabled:bg-warm-100 disabled:cursor-not-allowed"
                  >
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </section>

          {/* Profil + Firma + Adresse + Billing — nur im Edit-Modus,
              im Create-Modus reicht das Minimum (E-Mail + Name + Rolle). */}
          {isEdit && (
            <>
              <section>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2 flex items-center gap-1.5">
                  <UserCircle size={11} weight="bold" />
                  Persönliche Daten
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="admin-user-first-name">Vorname</Label>
                    <Input
                      id="admin-user-first-name"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Max"
                      data-testid="admin-user-form-first-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-user-last-name">Nachname</Label>
                    <Input
                      id="admin-user-last-name"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Mustermann"
                      data-testid="admin-user-form-last-name"
                    />
                  </div>
                </div>
              </section>

              <section>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2 flex items-center gap-1.5">
                  <Buildings size={11} weight="bold" />
                  Firma
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <Label htmlFor="admin-user-company">Firmenname</Label>
                    <Input
                      id="admin-user-company"
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="Beispiel GmbH"
                      data-testid="admin-user-form-company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-user-company-size">Größe / Tarif</Label>
                    <select
                      id="admin-user-company-size"
                      value={companySize}
                      onChange={(e) => setCompanySize(e.target.value)}
                      data-testid="admin-user-form-company-size"
                      className="w-full text-sm rounded-sm border border-ink-300 px-3 py-2 bg-white focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    >
                      {COMPANY_SIZES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Iter 19.68.17 — Company-Zuordnung (nur wenn Superadmin
                    das List-Endpoint erreichen konnte). N:1 via Dropdown. */}
                {companies.length > 0 && (
                  <div className="mt-3">
                    <Label htmlFor="admin-user-company-id">
                      Übergeordnetes Unternehmen (für Monatskontingent)
                    </Label>
                    <select
                      id="admin-user-company-id"
                      value={companyId}
                      onChange={(e) => setCompanyId(e.target.value)}
                      data-testid="admin-user-form-company-id"
                      className="w-full text-sm rounded-sm border border-ink-300 px-3 py-2 bg-white focus:border-brand-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400"
                    >
                      <option value="">— Kein Unternehmen (unlimitiert) —</option>
                      {companies.map((c) => {
                        const q = c.monthly_quota == null
                          ? "unlimitiert"
                          : c.monthly_quota === 0
                            ? "pausiert"
                            : `${c.monthly_quota}/Mon.`;
                        return (
                          <option key={c.id} value={c.id}>{c.name} ({q})</option>
                        );
                      })}
                    </select>
                  </div>
                )}
              </section>

              <section>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2 flex items-center gap-1.5">
                  <MapPin size={11} weight="bold" />
                  Adresse
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                  <div className="sm:col-span-4">
                    <Label htmlFor="admin-user-street">Straße + Nummer</Label>
                    <Input
                      id="admin-user-street"
                      type="text"
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                      placeholder="Hauptstraße 12"
                      data-testid="admin-user-form-street"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="admin-user-postal">PLZ</Label>
                    <Input
                      id="admin-user-postal"
                      type="text"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="1010"
                      data-testid="admin-user-form-postal"
                    />
                  </div>
                  <div className="sm:col-span-6">
                    <Label htmlFor="admin-user-city">Stadt</Label>
                    <Input
                      id="admin-user-city"
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Wien"
                      data-testid="admin-user-form-city"
                    />
                  </div>
                </div>
              </section>

              <section>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2 flex items-center gap-1.5">
                  <Receipt size={11} weight="bold" />
                  Rechnungsadresse
                </div>
                <label className="flex items-start gap-2 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={billingDifferent}
                    onChange={(e) => setBillingDifferent(e.target.checked)}
                    data-testid="admin-user-form-billing-different"
                    className="mt-0.5 accent-brand-500"
                  />
                  <span className="text-[12px] text-warm-700 leading-snug">
                    Abweichende Rechnungs-E-Mail verwenden (z.B. buchhaltung@firma.at).
                    Die postalische Adresse bleibt für die Rechnung dieselbe wie oben.
                  </span>
                </label>
                {billingDifferent && (
                  <div>
                    <Label htmlFor="admin-user-billing-email">Rechnungs-E-Mail</Label>
                    <Input
                      id="admin-user-billing-email"
                      type="email"
                      value={billingEmail}
                      onChange={(e) => setBillingEmail(e.target.value)}
                      placeholder="buchhaltung@firma.at"
                      data-testid="admin-user-form-billing-email"
                    />
                  </div>
                )}
              </section>
            </>
          )}

          {err && (
            <div
              role="alert"
              data-testid="admin-user-form-error"
              className="border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
            >
              {err}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 px-6 py-4 border-t border-ink-200 flex justify-end gap-3 bg-ink-50 rounded-b-sm">
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={onClose}
            disabled={submitting}
            data-testid="admin-user-form-cancel"
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="brand"
            size="md"
            disabled={submitting}
            data-testid="admin-user-form-submit"
          >
            {submitting
              ? "Wird gespeichert…"
              : isEdit
                ? "Änderungen speichern"
                : "Anlegen & E-Mail senden"}
          </Button>
        </div>
      </form>
    </div>
  );
}
